import type { CoachMemorySource } from "@/lib/coachSessionReviewContract";
import type { CoachDigitalTwinRuntime } from "@/lib/behavior/behaviorContracts";
import { buildCoachVeeraIdentityContextFromRuntime } from "@/lib/behavior/coachVeeraIdentityContext";
import { buildCoachVeeraRoute } from "@/lib/veeraRoutes";

export type CoachOverviewSignalType =
  | "sequence_check"
  | "graduation_review"
  | "engagement_trend"
  | "prolonged_practice";

export type CoachVeeraSignalType = "sequencing" | "graduation" | "engagement";

export interface CoachAiPageContextData {
  leaderName?: string;
  behaviorName?: string | null;
  phase?: string | null;
  coachRuntimeStatus: "configured" | "defaulted";
  coachLensName?: string | null;
  coachIdentityStatus: "active" | "draft";
  coachIdentityLensLabel: string | null;
  coachIdentityFrameworkPackId: string | null;
  coachIdentityFrameworkPackLabel: string | null;
  coachIdentityVoiceSummary: string | null;
  coachIdentityVoiceCustomizationStatus: "customized" | "framework_default" | "default";
  coachIdentityReferenceCount: number;
  coachIdentityParsedReferenceCount: number;
  coachTwinConfigured: boolean;
  coachMemoryAvailable: boolean;
  coachIdentityPreferredLanguage: string[];
  coachIdentityAvoidLanguage: string[];
  coachIdentityGuardrails: string[];
  coachIdentityQuestionStems: string[];
  coachIdentityPhraseBank: string[];
  coachIdentityCoachingMoves: string[];
  coachIdentityFrameworkSummary: string | null;
  coachIdentityFrameworkNotices: string[];
  coachIdentityFrameworkWatchouts: string[];
  coachIdentityFrameworkInterventionPatterns: string[];
  coachIdentityFrameworkProgressMarkers: string[];
  coachIdentityFrameworkStallSignals: string[];
  coachIdentityFrameworkNoticingSignals: string[];
  coachIdentityFrameworkReflectionPatterns: string[];
  coachIdentityFrameworkAllowedLanguage: string[];
  coachIdentityFrameworkProhibitedInferences: string[];
  coachIdentityFrameworkCoachOnlyBoundaries: string[];
  coachIdentityCoachLanguageHints: string[];
}

export function mapCoachOverviewSignalTypeToVeeraSignalType(signalType: CoachOverviewSignalType): CoachVeeraSignalType {
  if (signalType === "graduation_review") return "graduation";
  if (signalType === "sequence_check") return "sequencing";
  return "engagement";
}

export function normalizeCoachVeeraSignalType(rawSignalType?: string | null): CoachVeeraSignalType {
  const normalized = (rawSignalType || "").toLowerCase().trim();
  if (["graduation", "graduation_review", "graduation_check", "plateau_risk", "milestone"].includes(normalized)) {
    return "graduation";
  }
  if (["sequencing", "sequence_check", "sequencing_readiness"].includes(normalized)) {
    return "sequencing";
  }
  return "engagement";
}

export function buildCoachVeeraSignalInterpretRoute(params: {
  signalType: CoachOverviewSignalType;
  signalLevel: "low" | "medium" | "high";
  leaderId?: string | null;
  signals?: string[] | null;
}) {
  return buildCoachVeeraRoute({
    leaderId: params.leaderId,
    action: "signal-interpret",
    signalType: mapCoachOverviewSignalTypeToVeeraSignalType(params.signalType),
    signalLevel: params.signalLevel,
    signals: params.signals,
  });
}

export function buildCoachVeeraSignalInterpretPrompt(params: {
  signalType?: string | null;
  signalLevel?: string | null;
  signals?: string[] | null;
}) {
  const signalTypeLabel = normalizeCoachVeeraSignalType(params.signalType);
  const signalLevelLabel = params.signalLevel || "moderate";
  const signalList = (params.signals || []).map((part) => part.trim()).filter(Boolean);
  const signalContext = signalList.length > 0
    ? `Supporting signals: ${signalList.join(", ")}.`
    : "";

  return (
    `I'm reviewing a ${signalTypeLabel} signal for this leader (evidence level: ${signalLevelLabel}). ${signalContext}\n\n`
    + "Help me interpret this:\n"
    + "1. What patterns from recent moments are present in this leader's journey?\n"
    + `2. Why might these patterns matter for ${signalTypeLabel}?\n`
    + "3. What are the risks of acting too early vs waiting too long?\n"
    + "End with a reflective question to help me decide."
  );
}

export function buildCoachAiPageContextData(params: {
  activeLeader?: {
    name: string;
    behaviorName: string | null;
    phase: string | null;
  } | null;
  coachRuntime: CoachDigitalTwinRuntime;
  coachMemory: CoachMemorySource | null;
}): CoachAiPageContextData {
  const { activeLeader, coachRuntime, coachMemory } = params;
  const identityContext = buildCoachVeeraIdentityContextFromRuntime(coachRuntime);
  return {
    leaderName: activeLeader?.name,
    behaviorName: activeLeader?.behaviorName || null,
    phase: activeLeader?.phase || null,
    coachRuntimeStatus: coachRuntime.status,
    coachLensName: coachRuntime.philosophy.lens.lensName || null,
    coachIdentityStatus: coachRuntime.identity.activation.status,
    coachIdentityLensLabel: identityContext.lensLabel,
    coachIdentityFrameworkPackId: identityContext.frameworkPackId,
    coachIdentityFrameworkPackLabel: identityContext.frameworkPackLabel,
    coachIdentityVoiceSummary: identityContext.voiceSummary,
    coachIdentityVoiceCustomizationStatus: identityContext.voiceCustomizationStatus,
    coachIdentityReferenceCount: identityContext.referenceCount,
    coachIdentityParsedReferenceCount: identityContext.parsedReferenceCount,
    coachTwinConfigured: coachRuntime.voice.present,
    coachMemoryAvailable: Boolean(coachMemory),
    coachIdentityPreferredLanguage: identityContext.preferredLanguage,
    coachIdentityAvoidLanguage: identityContext.avoidLanguage,
    coachIdentityGuardrails: identityContext.guardrails,
    coachIdentityQuestionStems: identityContext.questionStems,
    coachIdentityPhraseBank: identityContext.phraseBank,
    coachIdentityCoachingMoves: identityContext.coachingMoves,
    coachIdentityFrameworkSummary: identityContext.frameworkSummary,
    coachIdentityFrameworkNotices: identityContext.frameworkNotices,
    coachIdentityFrameworkWatchouts: identityContext.frameworkWatchouts,
    coachIdentityFrameworkInterventionPatterns: identityContext.frameworkInterventionPatterns,
    coachIdentityFrameworkProgressMarkers: identityContext.frameworkProgressMarkers,
    coachIdentityFrameworkStallSignals: identityContext.frameworkStallSignals,
    coachIdentityFrameworkNoticingSignals: identityContext.frameworkNoticingSignals,
    coachIdentityFrameworkReflectionPatterns: identityContext.frameworkReflectionPatterns,
    coachIdentityFrameworkAllowedLanguage: identityContext.frameworkAllowedLanguage,
    coachIdentityFrameworkProhibitedInferences: identityContext.frameworkProhibitedInferences,
    coachIdentityFrameworkCoachOnlyBoundaries: identityContext.frameworkCoachOnlyBoundaries,
    coachIdentityCoachLanguageHints: identityContext.coachLanguageHints,
  };
}
